import { useState } from 'react';
import { callable } from '@/lib/firebase';
import { messageForAuthError } from '@/lib/authErrors';
import { strings } from '@/strings';
import type { NominationStatus } from '@/types/domain';

const recordContactOutcome = callable<
  { nominationId: string; outcome: string; note: string },
  { id: string; status: NominationStatus }
>('recordContactOutcome');

const promoteNomination = callable<{ nominationId: string }, { candidateId: string }>('promoteNomination');

const OUTCOMES: Array<{ value: string; label: string }> = [
  { value: 'contacted', label: strings.outcomeContacted },
  { value: 'interested', label: strings.outcomeInterested },
  { value: 'not_now', label: strings.outcomeNotNow },
  { value: 'declined', label: strings.outcomeDeclined },
  { value: 'rejected', label: strings.outcomeRejected },
];

const CLOSED: NominationStatus[] = ['promoted', 'rejected'];

/**
 * The call log, as a row of buttons. Promotion is offered only on an
 * `interested` nomination — the server refuses anything else, and showing a
 * button that is going to be refused teaches people to ignore refusals.
 */
export function OutcomeButtons({
  nominationId, status, onDone,
}: { nominationId: string; status: NominationStatus; onDone: () => void }) {
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (CLOSED.includes(status)) return null;

  async function act(fn: () => Promise<unknown>) {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await fn();
      setNote('');
      onDone();
    } catch (err) {
      setError(messageForAuthError((err as Error).message));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <label className="flex flex-col gap-1 text-xs text-muted" htmlFor={`note-${nominationId}`}>
        {strings.outcomeNote}
        <input
          id={`note-${nominationId}`}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className="rounded-md border border-border-subtle px-2 py-1 text-sm text-ink"
        />
      </label>
      <div className="flex flex-wrap gap-2">
        {OUTCOMES.map((outcome) => (
          <button
            key={outcome.value}
            type="button"
            disabled={busy}
            onClick={() => act(() => recordContactOutcome({ nominationId, outcome: outcome.value, note }))}
            className="min-h-11 rounded-md border border-border-subtle bg-white px-4 py-2 text-sm font-medium text-ink transition-colors hover:bg-surface disabled:cursor-not-allowed disabled:opacity-50"
          >
            {outcome.label}
          </button>
        ))}
        {status === 'interested' ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => act(() => promoteNomination({ nominationId }))}
            className="min-h-11 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50"
          >
            {strings.outcomePromote}
          </button>
        ) : null}
      </div>
      {error ? <p role="alert" className="text-sm text-danger">{error}</p> : null}
    </div>
  );
}
